<template>
    <div
        :data-component="$options.name"
        class="z-debug relative"
    >
        <cs-modal
            :visible="isVisible"
            :isFullscreen="!isScreenM"
            :channel="'real-estate'"
            :contentRendered="false"
            :headerText="'Anbieter:in kontaktieren'"
            @update:visible="emitClosedModalEvent"
        >
            <form
                v-if="!showSuccessView"
                id="form1"
                ref="formElement"
                novalidate
                @submit.prevent="onSubmit"
            >
                <div class="contentBox">
                    <cs-notification
                        v-show="showErrorNotification"
                        ref="notificationRef"
                        :notification-state="errorState"
                        class="mb-16"
                        >Deine Anfrage kann zur Zeit aufgrund eines technischen Problems nicht verschickt werden. Bitte
                        versuche es später erneut.
                        <template #headline>Es ist etwas schief gelaufen</template>
                    </cs-notification>
                    <p class="mb-16 text-h3 font-bold">Deine Anfrage</p>
                    <ms-progress-bar
                        class="block"
                        :numberOfSteps="numberOfSteps"
                        :currentStep="currentStep"
                        :progressSteps="progressSteps"
                    />
                </div>
                <div
                    v-if="offerType === OfferType.WAZ"
                    class="contentBox"
                >
                    <p class="text-text-l font-bold mb-16">Mietangaben</p>
                    <div
                        v-if="periodRentFieldInfoText"
                        class="mb-20"
                    >
                        <div class="flex text-text-l mb-4">
                            <p class="font-bold">Verfügbar:&nbsp;</p>
                            <p>{{ periodRentFieldInfoText.availability }}</p>
                        </div>
                        <div class="flex text-text-l">
                            <p class="font-bold">Mietdauer:&nbsp;</p>
                            <p>{{ periodRentFieldInfoText.periodOfLease }}</p>
                        </div>
                    </div>
                    <div data-clarity-mask="True">
                        <cs-input-field
                            v-model="rentStartDateValue"
                            class="mb-16"
                            :label="'Mietbeginn'"
                            @blur="updateProgress(values)"
                        />
                        <cs-input-counter
                            v-model="rentalPeriodValue"
                            class="mb-16"
                            label="Mietdauer (Monate)"
                            @update:modelValue="updateProgress(values)"
                        />
                        <cs-input-counter
                            v-model="maxPersonsValue"
                            class="mb-16"
                            label="Personen"
                            @update:modelValue="updateProgress(values)"
                        />
                    </div>
                </div>
                <div class="contentBox">
                    <p class="text-text-l font-bold mb-16">Nachricht verfassen*</p>
                    <div
                        v-if="contactInfo"
                        class="flex flex-col mb-16 text-text-l"
                    >
                        <span class="font-bold">{{ contactInfo.title }}</span>
                        <span v-if="contactInfo.name"> {{ contactInfo.name }}</span>
                    </div>
                    <cs-textarea
                        v-model="messageValue"
                        label="Nachricht"
                        data-clarity-mask="True"
                        :limit="2500"
                        :errorMsg="messageError"
                        :data-error="!!messageError"
                        :rows="6"
                        class="text-text-l"
                        @blur="updateProgress(values)"
                    />
                </div>
                <div class="contentBox">
                    <p class="text-text-l font-bold mb-16">Persönliche Angaben*</p>
                    <cs-radio-button
                        v-for="(option, key) in salutationOptions"
                        :key="option.value"
                        v-model="salutationValue"
                        name="salutation"
                        :value="option.value"
                        :showError="!!salutationError"
                        class="inline-flex mr-32 screen-m:mb-8"
                        :class="{ 'mb-8': key === SalutationKeys.OTHER }"
                        data-clarity-mask="True"
                        @update:modelValue="updateSalutationValue"
                        >{{ option.label }}</cs-radio-button
                    >
                    <div
                        v-if="salutationError"
                        class="pb-5 mb-20 -mt-8 text-caption text-color-alert-negative-100"
                        data-radio-error
                    >
                        {{ salutationError }}
                    </div>
                    <div
                        class="grid grid-cols-1 grid-rows-3 gap-16 screen-m:grid-cols-2 screen-m:grid-rows-2"
                        data-clarity-mask="True"
                    >
                        <cs-input-field
                            v-model="firstNameValue"
                            :label="'Vorname'"
                            :data-error="!!firstNameError"
                            :errorMsg="firstNameError"
                            @blur="updateProgress(values)"
                        />
                        <cs-input-field
                            v-model="lastNameValue"
                            :label="'Nachname'"
                            :errorMsg="lastNameError"
                            :data-error="!!lastNameError"
                            @blur="updateProgress(values)"
                        />
                        <cs-input-field
                            v-model="emailValue"
                            class="screen-m:col-span-2"
                            :label="'E-Mail-Adresse'"
                            :errorMsg="emailError"
                            :data-error="!!emailError"
                            @blur="updateProgress(values)"
                        />
                    </div>
                </div>
                <div class="contentBox">
                    <p class="text-text-l font-bold mb-16">Kontaktdaten</p>
                    <div
                        class="grid grid-cols-1 grid-rows-4 gap-16 screen-m:grid-cols-6 screen-m:grid-rows-2"
                        data-clarity-mask="True"
                    >
                        <cs-input-field
                            v-model="phoneValue"
                            class="col-span-6"
                            :label="'Telefonnummer'"
                            @blur="updateProgress(values)"
                        />
                        <cs-input-field
                            v-model="streetValue"
                            class="col-span-6"
                            :label="'Straße und Nr.'"
                            @blur="updateProgress(values)"
                        />
                        <cs-input-field
                            v-model="zipValue"
                            class="col-span-6 screen-m:col-span-2"
                            :label="'PLZ'"
                            @blur="updateProgress(values)"
                        />
                        <cs-input-field
                            v-model="cityValue"
                            class="col-span-6 screen-m:col-span-4"
                            :label="'Ort'"
                            @blur="updateProgress(values)"
                        />
                    </div>
                </div>
                <div class="contentBox">
                    <cs-checkbox
                        v-model="allowToStoreCheckbox"
                        name="localStorage"
                        value="true"
                        class="mb-8"
                        >Daten für zukünftige Anfragen speichern</cs-checkbox
                    >
                    <div v-if="privacyPoliceUrl">
                        Die
                        <cs-copy-text-link
                            :href="privacyPoliceUrl"
                            target="_blank"
                            >Datenschutzerklärung</cs-copy-text-link
                        >
                        habe ich zur Kenntnis genommen.
                    </div>
                </div>

                <div class="text-caption">Bitte fülle alle mit * gekennzeichneten Felder aus</div>
            </form>
            <div
                v-else
                class="flex flex-col items-center p-16 rounded-10 bg-white shadow"
                data-success-page
            >
                <ms-form-success class="mb-16 h-auto m-auto" />
                <ms-headline
                    type="h3"
                    :isSpan="true"
                    class="mb-8"
                    >Deine Anfrage wurde abgeschickt</ms-headline
                >
                <p class="text-text-l text-center">
                    Der:die Anbieter:in erhält deine Nachricht. Wir wünschen dir viel Erfolg. Nutze die Wartezeit, um
                    nach weiteren Immobilien zu suchen.
                </p>
            </div>
            <template
                v-if="!showSuccessView"
                #buttons
            >
                <cs-primary-button
                    class="w-full screen-m:w-fit"
                    :type="'submit'"
                    form="form1"
                    :isCentered="true"
                >
                    Anfrage absenden
                </cs-primary-button>
            </template>
        </cs-modal>
    </div>
</template>
<script lang="ts" src="./MsDetailModalContact.ts" />
<style scoped>
.contentBox {
    @apply p-16 mb-16 rounded-10 bg-white shadow;
}
</style>
